import { TFunction } from "i18next";
import formatErrorMessage from "src/utils/formatErrorMessage";

import { RelationalValue, useClassroomWebinarQuery, IClassroomResource } from "@sol/sdk";
import { message } from "@sol/uikit/feedback/Message/Message";

type Props = {
    classroom: RelationalValue<IClassroomResource>;
    t: TFunction;
};

/**
 * Validates that a meeting URL uses HTTPS protocol.
 * Blocks javascript:, data:, http:, and other unsafe schemes.
 * Returns the URL string if valid, null otherwise.
 */
export const sanitizeMeetingUrl = (url: string | undefined | null): string | null => {
    if (!url) {
        return null;
    }

    try {
        const parsed = new URL(url);
        if (parsed.protocol === "https:") {
            return parsed.href;
        }
        return null;
    } catch {
        return null;
    }
};

export const useOpenClassroomWebinar = ({ classroom, t }: Props) => {
    const [messageApi] = message.useMessage();

    const { refetch: fetchWebinar, isFetching: isLoading } = useClassroomWebinarQuery(
        { classroom },
        {
            enabled: false,
            retry: 0,
            onError: error => {
                messageApi.error(formatErrorMessage(error));
            },
        },
    );

    const openWebinar = async () => {
        const { data: webinar } = await fetchWebinar();
        if (webinar) {
            const safeUrl = sanitizeMeetingUrl(webinar.url);
            if (safeUrl) {
                window.open(safeUrl, "_blank", "noopener,noreferrer");
            } else {
                messageApi.error(t("errors.url"));
            }
        }
    };

    return { openWebinar, isLoading };
};
